import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clubService from "../../services/clubService";
import campusService from "../../services/campusService";
import universityService from "../../services/universityService";
import { useNotification } from "../../context/NotificationContext";
import AppButton from "../../components/ui/AppButton";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const ClubList = ({
  onEdit,
  onView,
  onManageMembers,
  onManageEvents,
  refreshTrigger,
}) => {
  const { t } = useTranslation("clubs");
  const { t: tCommon } = useTranslation("common");

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [selectedCampus, setSelectedCampus] = useState("");

  // Dropdown data
  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [universityLoading, setUniversityLoading] = useState(false);
  const [campusLoading, setCampusLoading] = useState(false);

  // University search states
  const [universitySearchTerm, setUniversitySearchTerm] = useState("");
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [universitySearchTimeout, setUniversitySearchTimeout] = useState(null);

  const { showSuccess } = useNotification();

  // Load universities for filter dropdown
  useEffect(() => {
    loadUniversities();
  }, []);

  // Load campuses when university changes
  useEffect(() => {
    if (selectedUniversity) {
      loadCampusesByUniversity(selectedUniversity);
    } else {
      setCampuses([]);
      setSelectedCampus("");
    }
  }, [selectedUniversity]);

  // University search effect
  useEffect(() => {
    if (universitySearchTimeout) {
      clearTimeout(universitySearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterUniversities();
    }, 300);

    setUniversitySearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universitySearchTerm, universities]);

  // Load clubs when filters change
  useEffect(() => {
    loadClubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, selectedCampus, refreshTrigger]);

  const loadUniversities = async () => {
    try {
      setUniversityLoading(true);
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        const sortedUniversities = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || ""
          )
        );
        setUniversities(sortedUniversities);
        setFilteredUniversities(sortedUniversities);
      }
    } catch (error) {
      console.error("Error loading universities:", error);
    } finally {
      setUniversityLoading(false);
    }
  };

  const filterUniversities = () => {
    if (!universitySearchTerm.trim()) {
      setFilteredUniversities(universities);
      return;
    }

    const filtered = universities.filter(
      (university) =>
        (university.nameEn || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase()) ||
        (university.nameLocal || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase())
    );
    setFilteredUniversities(filtered);
  };

  const loadCampusesByUniversity = async (universityId) => {
    try {
      setCampusLoading(true);
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId
      );
      if (response.success) {
        const sortedCampuses = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || ""
          )
        );
        setCampuses(sortedCampuses);
      }
    } catch (error) {
      console.error("Error loading campuses:", error);
    } finally {
      setCampusLoading(false);
    }
  };

  const loadClubs = async () => {
    try {
      setLoading(true);
      const response = await clubService.getClubs(
        currentPage,
        pageSize,
        searchTerm,
        selectedCampus
      );

      if (response.success) {
        console.log("Clubs response:", response.data.data);
        const mappedClubs = response.data.data.map((club) => ({
          ...club,
          active: club.status,
        }));
        setClubs(mappedClubs || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error("Error loading clubs:", error);
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (active) => {
    console.log("Status badge active value:", active, typeof active);
    const isActive = Boolean(active);
    return (
      <span
        className={`badge ${
          isActive ? "bg-success" : "bg-danger"
        } bg-opacity-10 text-${isActive ? "success" : "danger"} fw-bold`}
      >
        <i
          className={`fas ${
            isActive ? "fa-check-circle" : "fa-times-circle"
          } me-1`}
        ></i>
        {isActive ? tCommon("status.active") : tCommon("status.inactive")}
      </span>
    );
  };

  const handleBanToggle = async (club) => {
    const confirmMsg = club.active
      ? t("confirmDeactivate", { name: club.nameEn || club.name })
      : t("confirmActivate", { name: club.nameEn || club.name });

    if (window.confirm(confirmMsg)) {
      try {
        const action = club.active ? "ban" : "unban";
        setActionLoading((prev) => ({ ...prev, [club.id]: action }));

        let response;
        if (club.active) {
          response = await clubService.banClub(club.id);
        } else {
          response = await clubService.unbanClub(club.id);
        }

        if (response.success) {
          showSuccess(
            club.active
              ? tCommon("messages.operationSuccessful")
              : tCommon("messages.operationSuccessful")
          );
          loadClubs();
        }
      } catch (error) {
        console.error("Error toggling club status:", error);
      } finally {
        setActionLoading((prev) => ({ ...prev, [club.id]: null }));
      }
    }
  };

  const handleDelete = async (club) => {
    if (window.confirm(t("messages.confirmDelete"))) {
      try {
        setActionLoading((prev) => ({ ...prev, [club.id]: "delete" }));
        const response = await clubService.deleteClub(club.id);

        if (response.success) {
          showSuccess(t("messages.deleteSuccess"));
          loadClubs();
        }
      } catch (error) {
        console.error("Error deleting club:", error);
      } finally {
        setActionLoading((prev) => ({ ...prev, [club.id]: null }));
      }
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleUniversitySelect = (university) => {
    setSelectedUniversity(university.id);
    setUniversitySearchTerm(university.nameEn);
    setShowUniversityDropdown(false);
    setSelectedCampus("");
    setCurrentPage(0);
  };

  const handleUniversitySearchChange = (e) => {
    const value = e.target.value;
    setUniversitySearchTerm(value);
    setShowUniversityDropdown(true);

    if (!value.trim()) {
      setFilteredUniversities(universities);
    }
  };

  const handleUniversitySearchFocus = () => {
    setShowUniversityDropdown(true);
  };

  const handleUniversitySearchBlur = () => {
    setTimeout(() => setShowUniversityDropdown(false), 200);
  };

  const handleCampusChange = (e) => {
    setSelectedCampus(e.target.value);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedUniversity("");
    setSelectedCampus("");
    setUniversitySearchTerm("");
    setCurrentPage(0);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <AppButton
          key={i}
          variant={i === currentPage ? "primary" : "outline"}
          className="btn-sm mx-1"
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </AppButton>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          {tCommon("pagination.showingEntries", {
            start: currentPage * pageSize + 1,
            end: Math.min((currentPage + 1) * pageSize, totalElements),
            total: totalElements,
          })}
        </div>
        <div className="d-flex align-items-center">
          <AppButton
            variant="outline"
            className="btn-sm me-2"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {tCommon("buttons.previous")}
          </AppButton>
          {pages}
          <AppButton
            variant="outline"
            className="btn-sm ms-2"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            {tCommon("buttons.next")}
          </AppButton>
        </div>
      </div>
    );
  };

  return (
    <div className="club-list">
      <ListToolbar
        title={t("title")}
        subtitle={t("clubsFound", { count: totalElements })}
        filterBadges={
          (searchTerm || selectedUniversity || selectedCampus) ? (
            <>
              {searchTerm && (
                <span className="badge bg-primary">Search: "{searchTerm}"</span>
              )}
              {selectedUniversity && (
                <span className="badge bg-info">
                  {t("filters.filterByUniversity")}:{" "}
                  {universitySearchTerm ||
                    universities.find(
                      (u) => u.id.toString() === selectedUniversity
                    )?.nameEn}
                </span>
              )}
              {selectedCampus && (
                <span className="badge bg-info">
                  {t("filterByCampus")}:{" "}
                  {campuses.find((c) => c.id.toString() === selectedCampus)?.nameEn}
                </span>
              )}
            </>
          ) : null
        }
      >
        <div className="row g-3">
          {/* Search */}
          <div className="col-md-4">
            <label className="form-label fw-medium">{t("searchClubs")}</label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder={t("searchByClubName")}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <AppButton
                    as="button"
                    type="button"
                    variant="outline"
                    className="btn-outline-danger"
                    onClick={() => setSearchTerm("")}
                    title={tCommon("buttons.clear")}
                  >
                    <i className="fas fa-times"></i>
                  </AppButton>
                )}
              </div>
            </div>

            {/* University Filter */}
            <div className="col-md-4">
              <label className="form-label fw-medium">
                {t("filters.filterByUniversity")}
              </label>
              <div className="position-relative">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("searchUniversities")}
                    value={universitySearchTerm}
                    onChange={handleUniversitySearchChange}
                    onFocus={handleUniversitySearchFocus}
                    onBlur={handleUniversitySearchBlur}
                    disabled={universityLoading}
                  />
                  <AppButton
                    as="button"
                    variant="outline"
                    className="btn-outline-secondary"
                    type="button"
                    onClick={() =>
                      setShowUniversityDropdown(!showUniversityDropdown)
                    }
                    disabled={universityLoading}
                  >
                    <i className="fas fa-search"></i>
                  </AppButton>
                </div>

                {/* Hidden input for selected university */}
                <input type="hidden" value={selectedUniversity} />

                {/* Clear university button */}
                {selectedUniversity && (
                  <AppButton
                    as="button"
                    type="button"
                    variant="outline"
                    className="btn-sm position-absolute btn-outline-danger"
                    style={{
                      right: "50px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 10,
                    }}
                    onClick={() => {
                      setSelectedUniversity("");
                      setUniversitySearchTerm("");
                      setSelectedCampus("");
                    }}
                    title={tCommon("buttons.clear")}
                  >
                    <i className="fas fa-times"></i>
                  </AppButton>
                )}

                {universityLoading && (
                  <small className="text-muted">{t("loadingUniversities")}</small>
                )}

                {/* Dropdown for university selection */}
                {showUniversityDropdown && filteredUniversities.length > 0 && (
                  <div className="university-dropdown">
                    <button
                      type="button"
                      className="university-dropdown-item text-primary border-bottom"
                      onClick={() => {
                        setSelectedUniversity("");
                        setUniversitySearchTerm("");
                        setSelectedCampus("");
                        setShowUniversityDropdown(false);
                      }}
                    >
                      <div className="university-name-en">
                        <i className="fas fa-globe me-2"></i>
                        {t("filters.allUniversities")}
                      </div>
                    </button>
                    {filteredUniversities.map((university) => (
                      <button
                        key={university.id}
                        type="button"
                        className="university-dropdown-item"
                        onClick={() => handleUniversitySelect(university)}
                      >
                        <div className="university-name-en">
                          {university.nameEn}
                        </div>
                        <div className="university-name-local">
                          {university.nameLocal}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {showUniversityDropdown &&
                  filteredUniversities.length === 0 && (
                    <div className="university-dropdown">
                      <div className="university-dropdown-item text-muted">
                        {t("noUniversitiesFound")}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Campus Filter */}
            <div className="col-md-4">
              <label className="form-label fw-medium">{t("filterByCampus")}</label>
              <div className="position-relative">
                <select
                  className="form-select"
                  value={selectedCampus}
                  onChange={handleCampusChange}
                  disabled={campusLoading || !selectedUniversity}
                >
                  <option value="">
                    {selectedUniversity
                      ? t("allCampuses")
                      : t("selectUniversityFirst")}
                  </option>
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.nameEn} - {campus.nameLocal}
                    </option>
                  ))}
                </select>

                {/* Clear campus button */}
                {selectedCampus && (
                  <AppButton
                    as="button"
                    type="button"
                    variant="outline"
                    className="btn-sm position-absolute btn-outline-danger"
                    style={{
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 10,
                    }}
                    onClick={() => setSelectedCampus("")}
                    title={tCommon("buttons.clear")}
                  >
                    <i className="fas fa-times"></i>
                  </AppButton>
                )}
              </div>
            </div>

          <div className="col-md-3 d-flex align-items-end">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">{t("show")}:</label>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{ width: "auto" }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {(searchTerm || selectedUniversity || selectedCampus) && (
            <div className="col-auto d-flex align-items-end">
              <AppButton variant="outline" className="btn-sm" onClick={clearFilters}>
                <i className="fas fa-times me-1"></i>
                {tCommon("buttons.clearFilters")}
              </AppButton>
            </div>
          )}
        </div>
      </ListToolbar>

      {/* Clubs Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{tCommon("actions.loading")}</span>
              </div>
              <p className="text-muted mt-2">{t("loadingClubs")}</p>
            </div>
          ) : clubs.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-users text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">{t("noClubsFound")}</h5>
              <p className="text-muted">
                {searchTerm || selectedUniversity || selectedCampus
                  ? t("adjustFilters")
                  : t("noClubsCreated")}
              </p>
            </div>
          ) : (
            <AppTable responsive>
                <thead>
                  <tr>
                    <th className="ps-4">{t("title")}</th>
                    <th>{t("fields.campus")}</th>
                    <th>{t("contact")}</th>
                    <th className="text-center">{t("tabs.members")}</th>
                    <th className="text-center">{t("tabs.events")}</th>
                    <th className="text-center">{t("fields.status")}</th>
                    <th className="text-center pe-4">{tCommon("table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {clubs.map((club) => (
                    <tr key={club.id} className="border-bottom">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className="club-logo rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                            style={{ width: "50px", height: "50px" }}
                          >
                            {club.imageUrl ? (
                              <img
                                src={club.imageUrl}
                                alt={club.nameEn || club.name}
                                className="rounded-circle"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <i className="fas fa-users text-primary fs-4"></i>
                            )}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {club.nameEn || club.name}
                            </div>
                            <small className="text-muted">
                              {club.descriptionEn || club.description
                                ? (club.descriptionEn || club.description)
                                    .length > 50
                                  ? `${(
                                      club.descriptionEn || club.description
                                    ).substring(0, 50)}...`
                                  : club.descriptionEn || club.description
                                : t("noDescription")}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-dark fw-medium">
                          {club.campusNameEn}
                        </div>
                        <small className="text-muted">
                          {club.universityNameEn}
                        </small>
                      </td>
                      <td>
                        <div className="text-dark fw-medium">
                          {club.communication?.email ? (
                            <a
                              href={`mailto:${club.communication?.email}`}
                              className="text-decoration-none"
                            >
                              {club.communication?.email}
                            </a>
                          ) : (
                            <span className="text-muted">{t("noEmail")}</span>
                          )}
                        </div>
                        <small className="text-muted">
                          {(club.communication?.phone_number ||
                            club.communication?.phone) && (
                            <div>
                              {club.communication.phone_number ||
                                club.communication.phone}
                            </div>
                          )}
                        </small>
                      </td>
                      <td className="text-center">
                        <div className="d-flex flex-column align-items-center">
                          <span className="badge bg-info bg-opacity-10 text-info fw-bold mb-1">
                            {club.totalMembers || 0}
                          </span>
                          <small className="text-muted">
                            {club.totalAdmins || 0}{" "}
                            {t("admin", { count: club.totalAdmins || 0 })}
                          </small>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex flex-column align-items-center">
                          <span className="badge bg-success bg-opacity-10 text-success fw-bold mb-1">
                            {club.totalEvents || 0}
                          </span>
                          <small className="text-muted">
                            {club.upcomingEventsNumber || 0} {t("upcoming")}
                          </small>
                        </div>
                      </td>
                      <td className="text-center">
                        {getStatusBadge(club.active)}
                      </td>
                      <td className="text-center pe-4">
                        <div className="btn-group" role="group">
                          <AppButton
                            variant="outline"
                            className="btn-sm text-primary border-primary"
                            onClick={() => onView(club)}
                            title={tCommon("buttons.viewDetails")}
                          >
                            <i className="fas fa-eye"></i>
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className="btn-sm text-success border-success"
                            onClick={() => onEdit(club)}
                            title={tCommon("buttons.edit")}
                          >
                            <i className="fas fa-edit"></i>
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className="btn-sm text-warning border-warning"
                            onClick={() => handleBanToggle(club)}
                            disabled={
                              actionLoading[club.id] === "ban" ||
                              actionLoading[club.id] === "unban"
                            }
                            title={
                              club.active
                                ? tCommon("buttons.deactivate")
                                : tCommon("buttons.activate")
                            }
                          >
                            {actionLoading[club.id] === "ban" ||
                            actionLoading[club.id] === "unban" ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  {tCommon("actions.loading")}
                                </span>
                              </div>
                            ) : (
                              <i
                                className={`fas ${
                                  club.active ? "fa-toggle-off" : "fa-toggle-on"
                                }`}
                              ></i>
                            )}
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className="btn-sm text-danger border-danger"
                            onClick={() => handleDelete(club)}
                            disabled={actionLoading[club.id] === "delete"}
                            title={tCommon("buttons.delete")}
                          >
                            {actionLoading[club.id] === "delete" ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  {tCommon("actions.loading")}
                                </span>
                              </div>
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </AppButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
            </AppTable>
          )}
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default ClubList;
